import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { keyFindingsApi, KeyFinding, AddKeyFindingRequest } from '../services/keyFindingsApi';

interface KeyFindingsContextType {
  findings: KeyFinding[];
  findingsCount: number;
  isLoading: boolean;
  addFinding: (request: AddKeyFindingRequest) => void;
  removeFinding: (findingId: string) => void;
  clearAllFindings: () => void;
  /** Check if any finding exists for this event (whole-event or text selection) */
  isEventMarked: (eventId: string) => boolean;
  /** Get all findings for a specific event */
  getFindingsForEvent: (eventId: string) => KeyFinding[];
}

const KeyFindingsContext = createContext<KeyFindingsContextType | undefined>(undefined);

export const useKeyFindings = (): KeyFindingsContextType => {
  const context = useContext(KeyFindingsContext);
  if (context === undefined) {
    // Outside a provider — return inert defaults
    return {
      findings: [],
      findingsCount: 0,
      isLoading: false,
      addFinding: () => {},
      removeFinding: () => {},
      clearAllFindings: () => {},
      isEventMarked: () => false,
      getFindingsForEvent: () => [],
    };
  }
  return context;
};

interface KeyFindingsProviderProps {
  caseId: string;
  children: ReactNode;
}

export const KeyFindingsProvider: React.FC<KeyFindingsProviderProps> = ({ caseId, children }) => {
  const queryClient = useQueryClient();
  const queryKey = ['key-findings', caseId];

  const { data: findings = [], isLoading } = useQuery(
    queryKey,
    () => keyFindingsApi.getForCase(caseId),
    { enabled: !!caseId, staleTime: 30000, retry: 1 }
  );

  const addMutation = useMutation(
    (request: AddKeyFindingRequest) => keyFindingsApi.add(caseId, request),
    {
      onMutate: async (request) => {
        await queryClient.cancelQueries(queryKey);
        const previous = queryClient.getQueryData<KeyFinding[]>(queryKey);
        // Optimistic add
        const optimistic: KeyFinding = {
          id: `temp-${Date.now()}`,
          caseId,
          eventId: request.eventId || null,
          selectedText: request.selectedText || null,
          startOffset: request.startOffset ?? null,
          endOffset: request.endOffset ?? null,
          note: request.note || null,
          userId: '',
          eventHeadline: null,
          eventDate: null,
          eventType: null,
          createdAt: new Date().toISOString(),
        };
        queryClient.setQueryData<KeyFinding[]>(queryKey, (old = []) => [optimistic, ...old]);
        return { previous };
      },
      onError: (_err, _req, context) => {
        if (context?.previous) {
          queryClient.setQueryData(queryKey, context.previous);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  const removeMutation = useMutation(
    (findingId: string) => keyFindingsApi.remove(findingId),
    {
      onMutate: async (findingId) => {
        await queryClient.cancelQueries(queryKey);
        const previous = queryClient.getQueryData<KeyFinding[]>(queryKey);
        queryClient.setQueryData<KeyFinding[]>(queryKey, (old = []) =>
          old.filter(f => f.id !== findingId)
        );
        return { previous };
      },
      onError: (_err, _findingId, context) => {
        if (context?.previous) {
          queryClient.setQueryData(queryKey, context.previous);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries(queryKey);
      },
    }
  );

  const addFinding = useCallback((request: AddKeyFindingRequest) => {
    addMutation.mutate(request);
  }, [addMutation]);

  const removeFinding = useCallback((findingId: string) => {
    removeMutation.mutate(findingId);
  }, [removeMutation]);

  const clearAllFindings = useCallback(() => {
    findings.forEach(f => removeMutation.mutate(f.id));
  }, [findings, removeMutation]);

  const isEventMarked = useCallback((eventId: string) => {
    return findings.some(f => f.eventId === eventId);
  }, [findings]);

  const getFindingsForEvent = useCallback((eventId: string) => {
    return findings.filter(f => f.eventId === eventId);
  }, [findings]);

  return (
    <KeyFindingsContext.Provider
      value={{
        findings,
        findingsCount: findings.length,
        isLoading,
        addFinding,
        removeFinding,
        clearAllFindings,
        isEventMarked,
        getFindingsForEvent,
      }}
    >
      {children}
    </KeyFindingsContext.Provider>
  );
};
